import { HttpClient, HttpEvent, HttpParams, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { enpointCervinoWeb } from 'src/app/configuration/enpointsCervino';
import { ItemFilterModel } from 'src/app/models/filters/item-filter.model';
import { OrderImportModel } from 'src/app/models/imports/order-import';
import { LoadPreInvoicingModel } from 'src/app/models/invoicing/load-preinvoicing.models';
import { PreInvoicingAlertModel } from 'src/app/models/invoicing/pre-invoicing-alert.models';
import { OrderDetailModel } from 'src/app/models/preliminaries/order-detail.model';
import { OrderModel } from 'src/app/models/preliminaries/order.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { ArticleSelectionTableModel } from 'src/app/models/tables/archive/article-selection-table.model';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class OrderDetailService {

  private readonly _httpClient = inject(HttpClient);

  annulOrder(orderContract: OrderModel): Observable<OrderModel> {
    return this._httpClient.put<OrderModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.orderDetail.AnnulOrder}`,
      orderContract
    );
  }

  createOrderByQuotation(listBreakdowns: Array<number>, orderId: number | null): Observable<OrderModel> {
    return this._httpClient.post<OrderModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.orderDetail.CreateOrderByQuotation}/${orderId}`,
      listBreakdowns
    );
  }

  createOrderDetail(orderDetailContract: OrderDetailModel): Observable<OrderDetailModel> {
    return this._httpClient.post<OrderDetailModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.orderDetail.CreateOrderDetail}`,
      orderDetailContract
    );
  }

  createOrderDetails(orderDetails: Array<OrderDetailModel>, orderId: number): Observable<boolean> {
    return this._httpClient.post<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.orderDetail.CreateDetailsAsync}/${orderId}`,
      orderDetails
    );
  }

  deleteOrderDetail(orderDetailId: number, actionFromAdjustment: boolean): Observable<OrderDetailModel> {
    const params = new HttpParams().set('actionFromAdjustment', actionFromAdjustment.toString());
    return this._httpClient.delete<OrderDetailModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.orderDetail.DeleteOrderDetail}/${orderDetailId}`,
      { params: params }
    );
  }

  deleteSomeDetails(orderDetailIds: Array<number>, actionFromAdjustment: boolean): Observable<boolean> {
    const params = new HttpParams().set('actionFromAdjustment', actionFromAdjustment.toString());
    return this._httpClient.put<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.orderDetail.DeleteSomeDetails}`,
      orderDetailIds, { params: params }
    );
  }

  downloadGuideImport(): Observable<HttpEvent<Blob>> {
    return this._httpClient.request(new HttpRequest('GET', `${environment.apiCervinoBackend}${enpointCervinoWeb.orderDetail.DownloadGuideImport}`,
      null, { reportProgress: true, responseType: 'blob' })
    );
  }

  exportDetailConsultation(filters: ColumnResponseModel[], columnSort: string | null, ascendant: boolean): Observable<Blob> {
    const params = new HttpParams().set('columnSort', columnSort ?? '').set('ascendant', ascendant.toString());
    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.orderDetail.ExportDetailConsultation}`, filters, {
      params,
      responseType: 'blob'
    });
  }

  exportImportErrors(alerts: PreInvoicingAlertModel[]): Observable<Blob> {
    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.orderDetail.ExportImportErrors}`, alerts,
      { responseType: 'blob' }
    );
  }

  getArticlesForDetails(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[], orderId: number): Observable<PagedResponseModel<ArticleSelectionTableModel>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant).set('orderId', orderId);
    return this._httpClient.post<PagedResponseModel<ArticleSelectionTableModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.orderDetail.GetArticlesForDetailsAsync}`,
      filters, { params: params }
    );
  }

  getAllOrderDetailsWithPagination(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<any>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant);
    return this._httpClient.post<PagedResponseModel<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.orderDetail.GetAllOrderDetailsWithPaginationAsync}`,
      filters, { params: params }
    );
  }

  getColumnsForDetailConsultation(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.orderDetail.GetColumnsForDetailConsultationAsync}`);
  }

  getColumnsForDetailCaptureAsync(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.orderDetail.GetColumnsForDetailCaptureAsync}`);
  }

  getColumnsForArticles(orderId: number): Observable<ColumnResponseModel[]> {
    const params = new HttpParams().set('orderId', orderId);
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.orderDetail.GetColumnsForArticles}`,
      { params: params }
    );
  }

  getColumnsForOrderDetailSelection(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.orderDetail.GetColumnsForOrderDetailSelectionAsync}`);
  }

  getOrderDetailById(orderDetailId: number): Observable<OrderDetailModel> {
    return this._httpClient.get<OrderDetailModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.orderDetail.GetOrderDetailById}/${orderDetailId}`);
  }

  getOrderDetailByKeys(orderId: number, articleId: number): Observable<OrderDetailModel> {
    const params = new HttpParams().set('orderId', orderId).set('articleId', articleId);
    return this._httpClient.get<OrderDetailModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.orderDetail.GetOrderDetailByKeys}`,
      { params: params }
    );
  }

  getOrderDetailsForCaptureTable(page: number, limit: number, columnSort: string | null, ascendant: boolean, orderId: number): Observable<PagedResponseModel<any>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant).set('orderId', orderId);
    return this._httpClient.get<PagedResponseModel<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.orderDetail.GetOrderDetailsForCaptureTableAsync}`,
      { params: params }
    );
  }

  getOrderDetailsForSelection(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[], orderId: number): Observable<PagedResponseModel<any>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant).set('orderId', orderId);
    return this._httpClient.post<PagedResponseModel<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.orderDetail.GetOrderDetailsForSelectionAsync}`,
      filters, { params: params }
    );
  }

  getRentalUnitForSelect(): Observable<any> {
    return this._httpClient.get<any>(`${environment.apiCervinoBackend}${enpointCervinoWeb.orderDetail.GetRentalUnitForSelectAsync}`);
  }

  getStocksByArticleIdByOrder(orderContract: OrderModel, articleId: number): Observable<ItemFilterModel> {
    return this._httpClient.post<ItemFilterModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.orderDetail.GetStocksByArticleIdByOrder}/${articleId}`,
      orderContract
    );
  }

  importOrderDetails(orderImports: Array<OrderImportModel>): Observable<LoadPreInvoicingModel> {
    return this._httpClient.post<LoadPreInvoicingModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.orderDetail.ImportOrderDetailsAsync}`,
      orderImports
    );
  }

  updateCompromiseDateByOrderId(orderId: number, days: number): Observable<Array<OrderDetailModel>> {
    return this._httpClient.put<Array<OrderDetailModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.orderDetail.UpdateCompromiseDateByOrderId}/${orderId}/${days}`,
      null
    );
  }

  updateOrderDetail(orderDetailContract: OrderDetailModel): Observable<OrderDetailModel> {
    return this._httpClient.put<OrderDetailModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.orderDetail.UpdateOrderDetail}`,
      orderDetailContract
    );
  }

  updateOrderPrice(orderDetailContract: OrderDetailModel): Observable<OrderDetailModel> {
    return this._httpClient.put<OrderDetailModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.orderDetail.UpdateOrderPrice}`,
      orderDetailContract
    );
  }
}
